/**
 * Form App Embed Style Injector
 *
 * Detects when form app embeds are loading and injects a stylesheet
 * into their shadow DOM before they display.
 */

(function () {
  "use strict";

  // CSS to inject into the shadow DOM
  const EMBED_STYLES = `
    /* Add your custom styles here */
    /* Example styles - customize as needed */
    * {
      box-sizing: border-box;
    }

    ._formFieldContainer_1mxsl_5:has(textarea)::before {
      display: none;
    }

    ._formContainer_1q1d2_30 {
      overflow: visible !important;
    }
  `;

  /**
   * Injects a stylesheet into a shadow DOM
   * @param {ShadowRoot} shadowRoot - The shadow root to inject styles into
   * @param {string} css - The CSS content to inject
   */
  function injectStylesIntoShadowDOM(shadowRoot, css) {
    // Check if stylesheet already exists
    const existingStyle = shadowRoot.querySelector(
      "style[data-embed-injected]"
    );
    if (existingStyle) {
      return;
    }

    // Create and inject style element
    const style = document.createElement("style");
    style.setAttribute("data-embed-injected", "true");
    style.textContent = css;

    // Insert at the beginning of shadow DOM to ensure it loads first
    if (shadowRoot.firstChild) {
      shadowRoot.insertBefore(style, shadowRoot.firstChild);
    } else {
      shadowRoot.appendChild(style);
    }
  }

  /**
   * Checks if an element is a form app embed
   * @param {HTMLElement} element - The element to check
   * @returns {boolean}
   */
  function isFormAppEmbed(element) {
    // Check for Shopify app embed indicators
    return (
      element.tagName === "SHOPIFY-APP-BLOCK" ||
      element.hasAttribute("data-app-embed") ||
      element.id === "app-embed" ||
      element.classList.contains("app-embed") ||
      !!(element.shadowRoot && element.shadowRoot.querySelector("form"))
    );
  }

  /**
   * Processes an element that might be a form app embed
   * @param {HTMLElement} element - The element to process
   */
  function processEmbedElement(element) {
    // If element already has shadow root, inject styles immediately
    if (element.shadowRoot) {
      injectStylesIntoShadowDOM(element.shadowRoot, EMBED_STYLES);
      return;
    }

    // Watch for shadow root creation using MutationObserver
    const observer = new MutationObserver((mutations, obs) => {
      if (element.shadowRoot) {
        injectStylesIntoShadowDOM(element.shadowRoot, EMBED_STYLES);
        obs.disconnect();
      }
    });

    observer.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["shadowroot", "shadowrootmode"],
    });

    // Also check periodically as a fallback (shadow DOM might be created asynchronously)
    let attempts = 0;
    const maxAttempts = 50; // Check for up to 5 seconds (50 * 100ms)
    const checkInterval = setInterval(() => {
      attempts++;
      if (element.shadowRoot) {
        injectStylesIntoShadowDOM(element.shadowRoot, EMBED_STYLES);
        observer.disconnect();
        clearInterval(checkInterval);
      } else if (attempts >= maxAttempts) {
        observer.disconnect();
        clearInterval(checkInterval);
      }
    }, 100);
  }

  /**
   * Scans the DOM for form app embeds and processes them
   */
  function scanForEmbeds() {
    // Look for known app embed selectors
    const selectors = [
      "shopify-app-block",
      "[data-app-embed]",
      "#app-embed",
      ".app-embed",
    ];

    selectors.forEach((selector) => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          if (element instanceof HTMLElement && isFormAppEmbed(element)) {
            processEmbedElement(element);
          }
        });
      } catch (e) {
        // Selector might not be valid, continue
        console.warn("Form embed scanner: Invalid selector", selector, e);
      }
    });
  }

  /**
   * Watches for dynamically added form app embeds
   */
  function watchForNewEmbeds() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (
            node.nodeType === Node.ELEMENT_NODE &&
            node instanceof HTMLElement
          ) {
            // Check if the added node itself is an embed
            if (isFormAppEmbed(node)) {
              processEmbedElement(node);
            }

            // Check for embeds within the added node
            const embeds = node.querySelectorAll(
              "shopify-app-block, [data-app-embed], #app-embed, .app-embed"
            );
            embeds.forEach((embed) => {
              if (embed instanceof HTMLElement && isFormAppEmbed(embed)) {
                processEmbedElement(embed);
              }
            });
          }
        });
      });
    });

    observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Initialize the form embed style injector
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        scanForEmbeds();
        watchForNewEmbeds();
      });
    } else {
      // DOM is already ready
      scanForEmbeds();
      watchForNewEmbeds();
    }
  }

  // Start initialization
  init();
})();
